import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { ArrowRight, Terminal, AlertCircle } from "lucide-react";

const LEVELS = [
  { id: "beginner", label: "Beginner", desc: "I'm new to Cisco CLI." },
  { id: "intermediate", label: "Intermediate", desc: "I know routing/switching basics." },
  { id: "advanced", label: "Advanced", desc: "I want lab drills + scoring only." },
];

const AREAS = [
  { code: "fundamentals", name: "Network Fundamentals" },
  { code: "access", name: "Network Access (VLANs/STP)" },
  { code: "connectivity", name: "IP Connectivity (OSPF/routing)" },
  { code: "services", name: "IP Services (NAT/DHCP)" },
  { code: "security", name: "Security (ACLs/Port Security)" },
  { code: "automation", name: "Automation" },
];

export default function Onboarding() {
  const { saveOnboarding, setUser } = useAuth();
  const nav = useNavigate();
  const [level, setLevel] = useState("intermediate");
  const [examDate, setExamDate] = useState("");
  const [weak, setWeak] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const toggleArea = (code) =>
    setWeak((w) => (w.includes(code) ? w.filter((x) => x !== code) : [...w, code]));

  // Persist onboarding selections. Defense-in-depth against the "stuck on
  // personalize" bug: if the primary `saveOnboarding` call throws (cookie /
  // CORS / 5xx hiccup), we (a) show the user a real error message, (b)
  // retry once after a short delay, and (c) provide an "skip & continue"
  // escape hatch that flips `onboarded=true` client-side and proceeds —
  // the user is NEVER silently trapped here again.
  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    const payload = {
      experience_level: level || "intermediate",
      exam_date: examDate || null,
      weak_areas: weak,
    };
    try {
      await saveOnboarding(payload);
      try {
        sessionStorage.setItem(
          "ciscolab.justOnboarded",
          JSON.stringify({ weak_areas: weak, experience_level: level }),
        );
      } catch { /* ignore — banner is best-effort */ }
      nav("/dashboard");
    } catch (err1) {
      // First attempt failed — try one silent retry.
      try {
        await new Promise((r) => setTimeout(r, 600));
        await api.post("/auth/onboarding", payload);
        try {
          sessionStorage.setItem(
            "ciscolab.justOnboarded",
            JSON.stringify({ weak_areas: weak, experience_level: level }),
          );
        } catch { /* noop */ }
        // Optimistically mark the user as onboarded in context so
        // ProtectedRoute lets them through even if the response shape varies.
        setUser((u) => (u ? { ...u, onboarded: true, experience_level: level, weak_areas: weak } : u));
        nav("/dashboard");
        return;
      } catch (err2) {
        setErr(formatApiError(err2?.response?.data?.detail) || "Couldn't save your preferences. Try the skip button below — you can edit these later in your profile.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Escape hatch — always navigates. Best-effort backend update.
  const skip = async () => {
    setErr(null);
    setLoading(true);
    try {
      await saveOnboarding({
        experience_level: level || "intermediate",
        exam_date: null,
        weak_areas: [],
      });
    } catch { /* ignore — we'll mark client-side and continue */ }
    // Client-side fallback so ProtectedRoute lets the user through even if
    // the backend call above silently failed (network/cookie hiccup).
    setUser((u) => (u ? { ...u, onboarded: true } : u));
    setLoading(false);
    nav("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-7 h-7 flex items-center justify-center bg-[#00E5FF] text-black">
            <Terminal size={16} strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-lg">
            Hypex<span className="text-[#00E5FF]">Labs</span>
          </span>
        </div>

        <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono mb-2">
          // step 1 of 1 · personalize
        </div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-6">
          Tell us where you're at.
        </h1>

        <form onSubmit={submit} className="space-y-8" data-testid="onboarding-form">
          <div>
            <div className="text-sm font-semibold mb-3">Experience level</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {LEVELS.map((l) => (
                <button
                  type="button"
                  key={l.id}
                  onClick={() => setLevel(l.id)}
                  data-testid={`level-${l.id}`}
                  className={`text-left p-4 border transition-all ${
                    level === l.id
                      ? "border-[#00E5FF] bg-[#00E5FF]/5"
                      : "border-zinc-800 hover:border-zinc-600"
                  }`}
                >
                  <div className="font-display font-bold">{l.label}</div>
                  <div className="text-xs text-zinc-500 mt-1">{l.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold mb-1">When's your exam? We'll pace your labs.</div>
            <div className="text-xs text-zinc-500 mb-3">Optional — leave blank if undecided.</div>
            <input
              type="date"
              value={examDate}
              onChange={(e) => setExamDate(e.target.value)}
              className="bg-black border border-zinc-800 focus:border-[#00E5FF] px-3 py-2 text-sm outline-none font-mono"
              data-testid="onboarding-exam-date"
            />
          </div>

          <div>
            <div className="text-sm font-semibold mb-3">Weak areas (pick any)</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {AREAS.map((a) => (
                <button
                  type="button"
                  key={a.code}
                  onClick={() => toggleArea(a.code)}
                  data-testid={`area-${a.code}`}
                  className={`text-left px-4 py-2 border text-sm transition-colors ${
                    weak.includes(a.code)
                      ? "border-[#00FF41] text-[#00FF41] bg-[#00FF41]/5"
                      : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                  }`}
                >
                  {weak.includes(a.code) ? "✓ " : "◯ "}
                  {a.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-[#00E5FF] text-black font-semibold hover:bg-[#00B8CC] transition-colors flex items-center gap-2 disabled:opacity-50"
              data-testid="onboarding-submit"
            >
              {loading ? "saving…" : "enter the lab"} <ArrowRight size={16} />
            </button>
            <button
              type="button"
              onClick={skip}
              disabled={loading}
              className="text-sm text-zinc-400 hover:text-[#00E5FF] underline-offset-4 hover:underline font-mono transition-colors disabled:opacity-50"
              data-testid="onboarding-skip"
            >
              Skip for now
            </button>
          </div>
          {err && (
            <div
              data-testid="onboarding-error"
              role="alert"
              className="flex items-start gap-3 p-4 border border-[#EF4444]/40 bg-[#EF4444]/5"
            >
              <AlertCircle size={16} className="text-[#EF4444] shrink-0 mt-0.5" />
              <div className="text-xs font-mono text-[#EF4444] leading-relaxed">
                {err}
                {" "}
                <button
                  type="button"
                  onClick={skip}
                  className="underline hover:text-[#F87171]"
                  data-testid="onboarding-error-skip"
                >
                  Skip & continue →
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
